import InstagramIcon from "@mui/icons-material/Instagram";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TablePagination,
} from "@mui/material";
import React, { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import {
  HeaderTableCell,
  TableCell,
  TableContainer,
  TableHeaderRow,
  TableRow,
} from "@/components/molecules/Table/styled";
import ROUTE_URLS from "@/constants/route-urls";
import { formatPhoneNumber } from "@/helpers/phone-helpers";
import useLanguage from "@/contexts/language-context";
import { ConversationReportMessages } from "@/messages/conversation-report/conversation-report.messages";
import {
  ConversationReportStatus,
  IConversationReportRow,
} from "@/types/conversation-report.types";
import { Pagination } from "@/types/page.types";
import { PaginatedResponse } from "@/types/requests.types";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type Props = {
  data: PaginatedResponse<IConversationReportRow>;
  pagination: Pagination;
  messages: ConversationReportMessages;
};

function formatDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR");
}

function statusLabel(status: ConversationReportStatus, messages: ConversationReportMessages) {
  switch (status) {
    case "waiting":
      return messages.statusWaiting;
    case "closed":
      return messages.statusClosed;
    case "reactivated":
      return messages.statusReactivated;
    default:
      return messages.statusAttending;
  }
}

function statusColor(status: ConversationReportStatus): "info" | "warning" | "success" | "secondary" {
  switch (status) {
    case "waiting":
      return "warning";
    case "closed":
      return "success";
    case "reactivated":
      return "secondary";
    default:
      return "info";
  }
}

const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  info: { bg: "badgeColors.info.background", fg: "badgeColors.info.text" },
  warning: { bg: "badgeColors.warning.background", fg: "badgeColors.warning.text" },
  success: { bg: "badgeColors.success.background", fg: "badgeColors.success.text" },
  secondary: { bg: "badgeColors.secondary.background", fg: "badgeColors.secondary.text" },
};

function PlatformIcon({ name, size = 18 }: { name: string | null; size?: number }) {
  const lower = (name || "").toLowerCase();
  if (lower.includes("insta")) return <InstagramIcon sx={{ fontSize: size, color: "#E1306C" }} />;
  return <WhatsAppIcon sx={{ fontSize: size, color: "#25D366" }} />;
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function CircleBadge({ value, tone }: { value: React.ReactNode; tone: "error" | "light" | "success" | "warning" }) {
  const map = {
    error: { bg: "badgeColors.error.background", fg: "badgeColors.error.text" },
    light: { bg: "grey.200", fg: "grey.700" },
    success: { bg: "badgeColors.success.background", fg: "badgeColors.success.text" },
    warning: { bg: "badgeColors.warning.background", fg: "badgeColors.warning.text" },
  }[tone];
  return (
    <MDBox
      component="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: map.bg, color: map.fg, fontSize: 12, fontWeight: 700 }}
    >
      {value}
    </MDBox>
  );
}

function csatTone(value: number): "error" | "warning" | "success" {
  if (value >= 4) return "success";
  if (value >= 3) return "warning";
  return "error";
}

function splitDate(value: string | null) {
  const text = formatDate(value);
  if (text === "—") return <>—</>;
  const [d, t] = text.split(", ");
  return (
    <>
      {d}
      <br />
      {t?.slice(0, 5)}
    </>
  );
}

// Keeps the actions menu visible without horizontal scrolling.
const STICKY_RIGHT = {
  position: "sticky",
  right: 0,
  zIndex: 2,
  backgroundColor: "inherit",
} as const;

function ConversationReportTable({ data, pagination, messages }: Props) {
  const { pageNumber, pageSize, changePage, extend } = pagination;
  const { lang } = useLanguage();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; id: number } | null>(null);

  const openConversation = (id: number) => {
    const url = `${ROUTE_URLS.chat(lang)}?conversationId=${id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <TableContainer sx={{ overflowX: "auto", boxShadow: "none", border: "1px solid", borderColor: "grey.200" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableHeaderRow>
              {[
                messages.columnContact,
                messages.columnPlatform,
                messages.columnAttendant,
                messages.columnWorkflow,
                messages.columnQueue,
                messages.mainSubject,
                messages.columnStart,
                messages.columnLastReceived,
                messages.endDate,
                messages.columnUnread,
                messages.columnStatus,
                messages.columnCsat,
                messages.closedByHuman,
                messages.columnIssueSolved,
                messages.columnActions,
              ].map((label) => (
                <HeaderTableCell
                  key={label}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    whiteSpace: "normal",
                    lineHeight: 1.2,
                    px: 1,
                    ...(label === messages.columnActions ? STICKY_RIGHT : {}),
                  }}
                >
                  {label}
                </HeaderTableCell>
              ))}
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {data.items.map((row, index) => {
              const tone = STATUS_STYLE[statusColor(row.status)];
              return (
                <TableRow key={row.id} index={index} hover sx={{ "& td": { fontSize: 12.5, color: "dark.main" } }}>
                  <TableCell>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <PlatformIcon name={row.platformName} size={22} />
                      <MDBox>
                        <MDTypography variant="button" fontWeight="medium" display="block" sx={{ fontSize: 12.5 }}>
                          {row.contactName || "—"}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {row.mobilePhone ? formatPhoneNumber(row.mobilePhone) || "—" : "—"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </TableCell>
                  <TableCell>
                    <MDBox display="flex" alignItems="center" gap={0.75}>
                      <PlatformIcon name={row.platformName} />
                      {row.platformName ? capitalize(row.platformName) : "—"}
                    </MDBox>
                  </TableCell>
                  <TableCell>{row.userName || "-"}</TableCell>
                  <TableCell>{row.workflowName || "-"}</TableCell>
                  <TableCell>{row.queueName || "-"}</TableCell>
                  <TableCell>{row.mainSubjectName || "-"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{splitDate(row.startDate)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{splitDate(row.lastReceivedDate)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{splitDate(row.endDate ?? null)}</TableCell>
                  <TableCell align="center">
                    <CircleBadge value={row.unReadMessages} tone={row.unReadMessages > 0 ? "error" : "light"} />
                  </TableCell>
                  <TableCell>
                    <MDBox
                      component="span"
                      sx={{
                        display: "inline-block",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        bgcolor: tone.bg,
                        color: tone.fg,
                        fontSize: 11.5,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {statusLabel(row.status, messages)}
                    </MDBox>
                  </TableCell>
                  <TableCell align="center">
                    {row.csat == null ? "-" : <CircleBadge value={row.csat} tone={csatTone(Number(row.csat))} />}
                  </TableCell>
                  <TableCell align="center">
                    {row.closedByHuman == null ? "-" : row.closedByHuman ? messages.yes : messages.no}
                  </TableCell>
                  <TableCell align="center">
                    {row.issueSolved == null ? "" : row.issueSolved ? messages.yes : messages.no}
                  </TableCell>
                  <TableCell align="center" sx={{ ...STICKY_RIGHT, bgcolor: "inherit" }}>
                    <IconButton size="small" onClick={(e) => setMenu({ anchor: e.currentTarget, id: row.id })}>
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) openConversation(menu.id);
            setMenu(null);
          }}
        >
          {messages.openConversation}
        </MenuItem>
      </Menu>
      <MDBox>
        <TablePagination
          labelRowsPerPage={messages.rowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          component="div"
          count={data.totalCount}
          onRowsPerPageChange={(event) => extend(Number(event.target.value))}
          rowsPerPage={pageSize}
          page={pageNumber - 1}
          onPageChange={(_, page) => changePage(page)}
        />
      </MDBox>
    </>
  );
}

export default ConversationReportTable;
